// @flow
import React, { type Node } from 'react';
import { messageFormat } from '@clubmed/utils';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon';
import { type AvailabilityEnum } from 'Components/widgets/product/Product.mapper';

type RemainingStock = number | null;
type AvailabilityConfigItem = {
  label: string | { n: string, _1: string },
  color: string,
  icon?: IconName,
};
type AvailabilityConfig = {
  available: AvailabilityConfigItem,
  unavailable: AvailabilityConfigItem,
  unknown: AvailabilityConfigItem,
  remaining: AvailabilityConfigItem,
};
type Props = {
  availability: AvailabilityEnum,
  remainingStock: RemainingStock,
  config: AvailabilityConfig,
};

function computeAvailabilityStatus({
  availability,
  remainingStock,
}: {
  availability: AvailabilityEnum,
  remainingStock: RemainingStock,
}): $Keys<AvailabilityConfig> {
  switch (true) {
    case availability === 'UNAVAILABLE':
    case availability === 'AVAILABLE' && remainingStock === 0:
      return 'unavailable';
    case availability === 'AVAILABLE' && remainingStock !== null && +remainingStock < 10:
    case availability === 'PARTIAL':
      return 'remaining';
    case availability === 'AVAILABLE':
      return 'available';
    default:
      return 'unknown';
  }
}

export function Availability({ availability, remainingStock, config }: Props): Node {
  const availabilityStatus: $Keys<AvailabilityConfig> = computeAvailabilityStatus({
    availability,
    remainingStock,
  });
  const { label, color, icon } = config[availabilityStatus] || {};
  return label ? (
    <span
      data-testid="availability-status-wrapper"
      className={`text-${color} flex items-center justify-center`}
    >
      {icon && <Icon className="mr-1" name={icon} width="0.8rem" />}
      <span data-testid="availability-status-text" className="my-0">
        {messageFormat(label).format({ count: remainingStock })}
      </span>
    </span>
  ) : null;
}
